/**
 * @fileoverview Own cross-matter inspection of queued Trello work and explicit task-form entry. Backend queue identity owns saved intent/progress; `kc-internal-frontend/src/components/CaseDetails/TrelloTaskForm.js` owns focused editing. Filters and status groups describe recorded work without equating a queued instruction to acknowledged remote creation.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelect } from 'primereact/multiselect';
import Button from "../shared/AppPrimeButton";
import Chip from '../shared/AppPrimeChip';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { setRefreshTrelloTasks } from '../../store/CaseDetails/action';
import TrelloTaskForm from '../CaseDetails/TrelloTaskForm';
import { AppButton, AppPageHeader } from '../shared';
import './trello-tasks.css';
import AppIcon from "../shared/AppIcon";



function createDefaultFilters() {
  return {
    search: '',
    status: [],
    taskType: [],
    attorney: [],
    matter: [],
    startDate: null,
    endDate: null,
    deadlinePresence: ''
  };
}

const STATUS_OPTIONS = [
{ label: 'Needs Attention', value: 'needs_attention' },
{ label: 'Upcoming', value: 'upcoming' },
{ label: 'Created in Trello', value: 'created' }];


const DEADLINE_PRESENCE_OPTIONS = [
{ label: 'All Deadline States', value: '' },
{ label: 'Has Deadline', value: 'has_deadline' },
{ label: 'No Deadline', value: 'no_deadline' }];


const SECTION_CONFIG = {
  needs_attention: {
    title: 'Needs Attention',
    description: 'Queued tasks due now, overdue, or missing a schedule date.',
    icon: 'pi pi-exclamation-circle'
  },
  upcoming: {
    title: 'Upcoming',
    description: 'Queued tasks scheduled for a future Trello creation date.',
    icon: 'pi pi-clock'
  },
  created: {
    title: 'Created in Trello',
    description: 'Tasks already pushed out of the queue into Trello.',
    icon: 'pi pi-check-circle'
  }
};

const SUMMARY_CARD_CONFIG = [
{ key: 'needs_attention', label: 'Needs Attention', icon: 'pi pi-exclamation-circle' },
{ key: 'upcoming', label: 'Upcoming', icon: 'pi pi-clock' },
{ key: 'created', label: 'Created', icon: 'pi pi-check-circle' },
{ key: 'visible', label: 'Total Visible', icon: 'pi pi-list' }];


const DEFAULT_SECTION_ROWS = 10;



function normalizeString(value) {
  return String(value || '').trim();
}



function parseTaskDate(value) {
  if (!value) return null;
  const normalized = typeof value === 'string' && value.includes(' ') && !value.includes('T') ?
  value.replace(' ', 'T') :
  value;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}



function startOfDay(value) {
  if (!value) return null;
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}



function endOfDay(value) {
  if (!value) return null;
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
}



function compareNullableDates(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a.getTime() - b.getTime();
}



function formatDisplayDate(value, fallback = 'No schedule date') {
  const date = parseTaskDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}



function truncateText(text, maxLength = 180) {
  const safeText = normalizeString(text);
  if (safeText.length <= maxLength) return safeText;
  return `${safeText.slice(0, maxLength - 3)}...`;
}



export function getTaskQueueStatus(task, now = new Date()) {
  const todayStart = startOfDay(now);
  const scheduleDate = parseTaskDate(task?.Date);
  const scheduleDay = scheduleDate ? startOfDay(scheduleDate) : null;

  if (task?.Popped) {
    return {
      key: 'created',
      label: 'Created in Trello',
      severity: 'success',
      isOverdue: false,
      isMissingDate: false,
      scheduleDate
    };
  }

  if (!scheduleDate) {
    return {
      key: 'needs_attention',
      label: 'Needs Attention',
      severity: 'warning',
      isOverdue: false,
      isMissingDate: true,
      scheduleDate: null
    };
  }

  if (scheduleDay <= todayStart) {
    return {
      key: 'needs_attention',
      label: 'Needs Attention',
      severity: scheduleDay < todayStart ? 'danger' : 'warning',
      isOverdue: scheduleDay < todayStart,
      isMissingDate: false,
      scheduleDate
    };
  }

  return {
    key: 'upcoming',
    label: 'Upcoming',
    severity: 'info',
    isOverdue: false,
    isMissingDate: false,
    scheduleDate
  };
}



function sortTasksForSection(tasks, sectionKey) {
  const sorted = [...tasks];

  return sorted.sort(

  (left, right) => {
    const leftDate = parseTaskDate(left.Date);
    const rightDate = parseTaskDate(right.Date);

    if (sectionKey === 'created') {
      const dateComparison = compareNullableDates(rightDate, leftDate);
      if (dateComparison !== 0) return dateComparison;
    } else {
      const dateComparison = compareNullableDates(leftDate, rightDate);
      if (dateComparison !== 0) return dateComparison;
    }

    return normalizeString(left.Name).localeCompare(normalizeString(right.Name));
  });
}



export function filterTasks(tasks, filters, now = new Date()) {
  const searchValue = normalizeString(filters.search).toLowerCase();
  const startDateValue = startOfDay(filters.startDate);
  const endDateValue = endOfDay(filters.endDate);

  return tasks.filter(

  (task) => {
    const status = getTaskQueueStatus(task, now);
    const scheduleDate = parseTaskDate(task.Date);
    const deadlineDate = parseTaskDate(task.Deadline);
    const haystack = [
    task.Name,
    task.Details,
    task.MatterNumber,
    task.AttorneyName]
    .map((value) => normalizeString(value).toLowerCase());

    if (searchValue && !haystack.some((value) => value.includes(searchValue))) {
      return false;
    }

    if (filters.status.length > 0 && !filters.status.includes(status.key)) {
      return false;
    }

    if (filters.taskType.length > 0 && !filters.taskType.includes(normalizeString(task.TaskType))) {
      return false;
    }

    if (filters.attorney.length > 0 && !filters.attorney.includes(normalizeString(task.AttorneyName))) {
      return false;
    }

    if (filters.matter.length > 0 && !filters.matter.includes(normalizeString(task.MatterNumber))) {
      return false;
    }

    if (filters.deadlinePresence === 'has_deadline' && !deadlineDate) {
      return false;
    }

    if (filters.deadlinePresence === 'no_deadline' && deadlineDate) {
      return false;
    }

    if (startDateValue) {
      if (!scheduleDate || scheduleDate < startDateValue) {
        return false;
      }
    }

    if (endDateValue) {
      if (!scheduleDate || scheduleDate > endDateValue) {
        return false;
      }
    }

    return true;
  });
}



export function buildTaskSections(tasks, now = new Date()) {
  const sections = {
    needs_attention: [],
    upcoming: [],
    created: []
  };

  tasks.forEach((task) => {
    const status = getTaskQueueStatus(task, now);
    sections[status.key].push(task);
  });

  return Object.entries(sections)
  .map(([key, sectionTasks]) => ({
    key,
    ...SECTION_CONFIG[key],
    tasks: sortTasksForSection(sectionTasks, key)
  }))
  .filter((section) => section.tasks.length > 0);
}



function buildDistinctOptions(tasks, field) {
  return [...new Set(tasks
  .map((task) => normalizeString(task[field]))
  .filter(Boolean))]
  .sort((left, right) => left.localeCompare(right))
  .map((value) => ({ label: value, value }));
}



function CountCard({ label, value, icon, tone }) {
  return (
    <div className={`trello-summary-card trello-summary-card--${tone}`}>
            <dt className="trello-summary-card__label">{label}</dt>
            <div className="trello-summary-card__value-row">
                <dd className="trello-summary-card__value">{value}</dd>
                <AppIcon icon={`${icon} trello-summary-card__icon`} aria-hidden="true" />
            </div>
        </div>);

}



function TaskSection({ section, onTaskClick, now }) {
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_SECTION_ROWS);

  useEffect(() => {
    if (first >= section.tasks.length && first !== 0) {
      setFirst(0);
    }
  }, [first, section.tasks.length]);

  const visibleTasks = useMemo(
    () => section.tasks.slice(first, first + rows),
    [first, rows, section.tasks]
  );

  return (
    <section className="trello-section" aria-labelledby={`trello-section-${section.key}`}>
            <div className="trello-section__header">
                <div>
                    <div className="trello-section__title-row">
                        <AppIcon icon={`${section.icon} trello-section__icon`} aria-hidden="true" />
                        <h2 id={`trello-section-${section.key}`} className="trello-section__title">{section.title}</h2>
                        <span className="trello-section__count">{section.tasks.length}</span>
                    </div>
                    <p className="trello-section__description">{section.description}</p>
                </div>
            </div>

            <div className="trello-section__list">
                {visibleTasks.map((task) =>
        <TaskRow
          key={task.id || `${normalizeString(task.MatterNumber)}-${normalizeString(task.Name)}-${normalizeString(task.Date)}`}
          task={task}
          status={getTaskQueueStatus(task, now)}
          onClick={() => onTaskClick(task)} />

        )}
            </div>

            {section.tasks.length > rows &&
      <div className="trello-section__pagination">
                    <Paginator
          first={first}
          rows={rows}
          totalRecords={section.tasks.length}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(event) => {
            setFirst(event.first);
            setRows(event.rows);
          }}
          template="RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          className="trello-section__paginator" />

                </div>}

        </section>);

}



function TaskRow({ task, status, onClick }) {
  const matterNumber = normalizeString(task.MatterNumber) || 'No matter';
  const attorneyName = normalizeString(task.AttorneyName) || 'Unassigned';
  const taskType = normalizeString(task.TaskType) || 'General';
  const details = truncateText(task.Details || 'No details provided.', 220);

  return (
    <button
      type="button"
      className={[
      'trello-task-row',
      status.key === 'created' ? 'trello-task-row--created' : '',
      status.isOverdue ? 'trello-task-row--overdue' : '',
      status.isMissingDate ? 'trello-task-row--missing-date' : '']
      .filter(Boolean).join(' ')}
      onClick={onClick}>

            <div className="trello-task-row__content">
                <div className="trello-task-row__topline">
                    <div className="trello-task-row__title-group">
                        <span className="trello-task-row__matter">{matterNumber}</span>
                        <h3 className="trello-task-row__title">{normalizeString(task.Name) || 'Untitled Task'}</h3>
                    </div>
                    <div className="trello-task-row__tags">
                        <Tag value={status.label} severity={status.severity} />
                        {status.isOverdue && <Tag value="Overdue" severity="danger" />}
                        {status.isMissingDate && <Tag value="No Schedule Date" severity="warning" />}
                    </div>
                </div>

                <div className="trello-task-row__meta">
                    <span><AppIcon icon="pi pi-user" aria-hidden="true" /> {attorneyName}</span>
                    <span><AppIcon icon="pi pi-calendar" aria-hidden="true" /> Scheduled {formatDisplayDate(task.Date)}</span>
                    {task.Deadline &&
          <span><AppIcon icon="pi pi-flag" aria-hidden="true" /> Deadline {formatDisplayDate(task.Deadline)}</span>}

                    <span><AppIcon icon="pi pi-tag" aria-hidden="true" /> {taskType}</span>
                </div>

                <p className="trello-task-row__details">{details}</p>
            </div>

            <AppIcon icon="pi pi-chevron-right trello-task-row__chevron" aria-hidden="true" />
        </button>);

}



function TrelloTasksFilters({
  filters,
  onFilterChange,
  onClearAll,
  filteredCount,
  totalCount,
  taskTypeOptions,
  attorneyOptions,
  matterOptions,
  loading
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusLabelMap = useMemo(() => Object.fromEntries(STATUS_OPTIONS.map((option) => [option.value, option.label])), []);
  const deadlineLabelMap = useMemo(() => Object.fromEntries(DEADLINE_PRESENCE_OPTIONS.map((option) => [option.value, option.label])), []);

  const activeFilters = useMemo(

  () => {
    const active = [];

    if (filters.search) {
      active.push({ key: 'search', label: `"${filters.search}"`, icon: 'pi pi-search' });
    }
    if (filters.status.length > 0) {
      active.push({
        key: 'status',
        label: filters.status.map((value) => statusLabelMap[value] || value).join(', '),
        icon: 'pi pi-flag'
      });
    }
    if (filters.taskType.length > 0) {
      active.push({ key: 'taskType', label: filters.taskType.join(', '), icon: 'pi pi-tag' });
    }
    if (filters.attorney.length > 0) {
      active.push({ key: 'attorney', label: filters.attorney.join(', '), icon: 'pi pi-user' });
    }
    if (filters.matter.length > 0) {
      active.push({ key: 'matter', label: filters.matter.join(', '), icon: 'pi pi-briefcase' });
    }
    if (filters.startDate) {
      active.push({ key: 'startDate', label: `From ${formatDisplayDate(filters.startDate)}`, icon: 'pi pi-calendar-plus' });
    }
    if (filters.endDate) {
      active.push({ key: 'endDate', label: `Through ${formatDisplayDate(filters.endDate)}`, icon: 'pi pi-calendar-minus' });
    }
    if (filters.deadlinePresence) {
      active.push({
        key: 'deadlinePresence',
        label: deadlineLabelMap[filters.deadlinePresence] || filters.deadlinePresence,
        icon: 'pi pi-clock'
      });
    }

    return active;
  }, [deadlineLabelMap, filters, statusLabelMap]);

  const hasActiveFilters = activeFilters.length > 0;

  const handleRemoveFilter =

  (filterKey) => {
    if (filterKey === 'search') {
      onFilterChange('search', '');
      return;
    }

    if (filterKey === 'deadlinePresence') {
      onFilterChange('deadlinePresence', '');
      return;
    }

    if (filterKey === 'startDate' || filterKey === 'endDate') {
      onFilterChange(filterKey, null);
      return;
    }

    onFilterChange(filterKey, []);
  };

  return (
    <div className="filters-wrapper">
            <div className="filters-header">
                <div className="filters-title-section">
                    <AppIcon icon="pi pi-filter filters-icon" />
                    <h2 className="filters-title">Filters</h2>
                    {hasActiveFilters && <span className="filter-count-badge">{activeFilters.length}</span>}
                </div>

                <div className="filters-actions">
                    <span className="filter-results-count">Showing {filteredCount} of {totalCount} tasks</span>

                    {hasActiveFilters &&
          <Button
            label="Clear All"
            icon="pi pi-times"
            className="p-button-text p-button-sm clear-filters-btn-header"
            onClick={onClearAll} />}



                    <Button
            icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
            className="p-button-text p-button-sm filters-toggle-btn"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'} />

                </div>
            </div>

            {isExpanded &&
      <div className="filters-content">
                    {hasActiveFilters &&
        <div className="active-filters-chips" role="region" aria-label="Active filters">
                            {activeFilters.map((filter) =>
          <Chip
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            removable
            onRemove={() => handleRemoveFilter(filter.key)}
            className="filter-chip" />

          )}
                        </div>}


                    <div className="filters-grid">
                        <div className="filter-group" data-filter-active={Boolean(filters.search)}>
                            <label htmlFor="trello-search-filter" className="filter-label">
                                <AppIcon icon="pi pi-search filter-label-icon" />
                                SEARCH
                            </label>
                            <InputText
              id="trello-search-filter"
              value={filters.search}
              onChange={(event) => onFilterChange('search', event.target.value)}
              placeholder="Task, details, matter, attorney..."
              disabled={loading}
              className={filters.search ? 'filter-active' : ''} />

                        </div>

                        <div className="filter-group" data-filter-active={filters.status.length > 0}>
                            <label htmlFor="trello-status-filter" className="filter-label">
                                <AppIcon icon="pi pi-flag filter-label-icon" />
                                STATUS
                            </label>
                            <MultiSelect
              id="trello-status-filter"
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(event) => onFilterChange('status', event.value || [])}
              placeholder="All Statuses"
              disabled={loading}
              className={filters.status.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.taskType.length > 0}>
                            <label htmlFor="trello-task-type-filter" className="filter-label">
                                <AppIcon icon="pi pi-tag filter-label-icon" />
                                TASK TYPE
                            </label>
                            <MultiSelect
              id="trello-task-type-filter"
              value={filters.taskType}
              options={taskTypeOptions}
              onChange={(event) => onFilterChange('taskType', event.value || [])}
              filter
              placeholder="All Task Types"
              disabled={loading}
              className={filters.taskType.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.attorney.length > 0}>
                            <label htmlFor="trello-attorney-filter" className="filter-label">
                                <AppIcon icon="pi pi-user filter-label-icon" />
                                ATTORNEY
                            </label>
                            <MultiSelect
              id="trello-attorney-filter"
              value={filters.attorney}
              options={attorneyOptions}
              onChange={(event) => onFilterChange('attorney', event.value || [])}
              filter
              placeholder="All Attorneys"
              disabled={loading}
              className={filters.attorney.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={filters.matter.length > 0}>
                            <label htmlFor="trello-matter-filter" className="filter-label">
                                <AppIcon icon="pi pi-briefcase filter-label-icon" />
                                MATTER
                            </label>
                            <MultiSelect
              id="trello-matter-filter"
              value={filters.matter}
              options={matterOptions}
              onChange={(event) => onFilterChange('matter', event.value || [])}
              filter
              placeholder="All Matters"
              disabled={loading}
              className={filters.matter.length > 0 ? 'filter-active' : ''}
              maxSelectedLabels={2}
              showClear />

                        </div>

                        <div className="filter-group" data-filter-active={Boolean(filters.startDate)}>
                            <label htmlFor="trello-start-date-filter" className="filter-label">
                                <AppIcon icon="pi pi-calendar-plus filter-label-icon" />
                                START DATE
                            </label>
                            <Calendar
              id="trello-start-date-filter"
              value={filters.startDate}
              onChange={(event) => onFilterChange('startDate', event.value)}
              placeholder="Scheduled on or after"
              disabled={loading}
              showIcon
              className={filters.startDate ? 'filter-active' : ''} />

                        </div>

                        <div className="filter-group" data-filter-active={Boolean(filters.endDate)}>
                            <label htmlFor="trello-end-date-filter" className="filter-label">
                                <AppIcon icon="pi pi-calendar-minus filter-label-icon" />
                                END DATE
                            </label>
                            <Calendar
              id="trello-end-date-filter"
              value={filters.endDate}
              onChange={(event) => onFilterChange('endDate', event.value)}
              placeholder="Scheduled on or before"
              disabled={loading}
              showIcon
              className={filters.endDate ? 'filter-active' : ''} />

                        </div>

                        <div className="filter-group" data-filter-active={Boolean(filters.deadlinePresence)}>
                            <label htmlFor="trello-deadline-presence-filter" className="filter-label">
                                <AppIcon icon="pi pi-clock filter-label-icon" />
                                DEADLINE
                            </label>
                            <Dropdown
              id="trello-deadline-presence-filter"
              value={filters.deadlinePresence}
              options={DEADLINE_PRESENCE_OPTIONS}
              onChange={(event) => onFilterChange('deadlinePresence', event.value)}
              placeholder="All Deadline States"
              disabled={loading}
              className={filters.deadlinePresence ? 'filter-active' : ''} />

                        </div>
                    </div>
                </div>}

        </div>);

}



function AllTrelloTasks() {
  const dispatch = useDispatch();
  const refreshTrelloTasks = useSelector((state) => state.caseDetails.refreshTrelloTasks);
  const matter = useSelector((state) => state.caseDetails.matter);
  const showToast = useContext(ToastContext);

  const [queuedTasks, setQueuedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [currentlyEditingTrelloTask, setCurrentlyEditingTrelloTask] = useState(null);
  const [trelloTaskFormVisible, setTrelloTaskFormVisible] = useState(false);

  const getTasks = useCallback(

  async () => {
    setLoading(true);

    try {
      const response = await api.get('/get_trello_tasks?');
      if (response.status === 200) {
        setQueuedTasks(response.data || []);
      } else {
        showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' });
      }
    } catch (error) {
      console.error('Error loading Trello tasks:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Unable to load Trello tasks' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    document.title = 'Trello Tasks';
    getTasks();
  }, [getTasks]);

  useEffect(() => {
    if (refreshTrelloTasks) {
      getTasks();
      dispatch(setRefreshTrelloTasks(false));
    }
  }, [dispatch, getTasks, refreshTrelloTasks]);

  const handleFilterChange = useCallback(

  (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClearAllFilters = useCallback(

  () => {
    setFilters(createDefaultFilters());
  }, []);

  const openTrelloTaskFormInEditMode = useCallback(

  (queuedTask) => {
    setCurrentlyEditingTrelloTask(queuedTask);
    setTrelloTaskFormVisible(true);
  }, []);

  const openNewTaskForm = useCallback(

  () => {
    setCurrentlyEditingTrelloTask(null);
    setTrelloTaskFormVisible(true);
  }, []);

  const referenceNow = useMemo(() => new Date(), [filters, queuedTasks]);
  const filteredTasks = useMemo(() => filterTasks(queuedTasks, filters, referenceNow), [filters, queuedTasks, referenceNow]);
  const sections = useMemo(() => buildTaskSections(filteredTasks, referenceNow), [filteredTasks, referenceNow]);

  const summaryCounts = useMemo(() => {
    const counts = { needs_attention: 0, upcoming: 0, created: 0, visible: filteredTasks.length };
    filteredTasks.forEach((task) => {
      const status = getTaskQueueStatus(task, referenceNow);
      counts[status.key] += 1;
    });
    return counts;
  }, [filteredTasks, referenceNow]);

  const taskTypeOptions = useMemo(() => buildDistinctOptions(queuedTasks, 'TaskType'), [queuedTasks]);
  const attorneyOptions = useMemo(() => buildDistinctOptions(queuedTasks, 'AttorneyName'), [queuedTasks]);
  const matterOptions = useMemo(() => buildDistinctOptions(queuedTasks, 'MatterNumber'), [queuedTasks]);

  const emptyStateMessage = queuedTasks.length === 0 ?
  'No queued Trello tasks yet.' :
  'No tasks match the current filters.';

  return (
    <>
            <TrelloTaskForm
        substage={matter?.Substage}
        visible={trelloTaskFormVisible}
        setVisible={setTrelloTaskFormVisible}
        currentlyEditing={currentlyEditingTrelloTask} />


            <div className="trello-tasks">
                <AppPageHeader
          eyebrow="Task Queue"
          title="Trello Tasks"
          icon="pi pi-check-square text-xl"
          actions={
          <AppButton
            label="Queue Trello Task"
            icon="pi pi-plus"
            onClick={openNewTaskForm}
            variant="secondary"
            className="trello-tasks__header-action"
            title="Queue a new Trello task" />} />




                <section className="trello-tasks__page" aria-label="Trello task queue workspace">
                    {!matter?.MatterNumber &&
          <div className="trello-tasks__helper">
                            Queue a task from any matter on this screen. If no case is open, select the matter and attorney in the modal before submitting.
                        </div>}


                    <dl className="trello-tasks__summary-grid" aria-label="Visible task summary">
                        {SUMMARY_CARD_CONFIG.map((card) =>
            <CountCard
              key={card.key}
              label={card.label}
              value={summaryCounts[card.key]}
              icon={card.icon}
              tone={card.key} />

            )}
                    </dl>

                    <TrelloTasksFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
            filteredCount={filteredTasks.length}
            totalCount={queuedTasks.length}
            taskTypeOptions={taskTypeOptions}
            attorneyOptions={attorneyOptions}
            matterOptions={matterOptions}
            loading={loading} />


                    {loading ?
          <div className="trello-tasks__loading">
                            <ProgressSpinner />
                        </div> :
          filteredTasks.length === 0 ?
          <div className="trello-tasks__empty">
                            <AppIcon icon="pi pi-inbox" aria-hidden="true" />
                            <p>{emptyStateMessage}</p>
                        </div> :

          <div className="trello-tasks__sections">
                            {sections.map((section) =>
            <TaskSection
              key={section.key}
              section={section}
              onTaskClick={openTrelloTaskFormInEditMode}
              now={referenceNow} />

            )}
                        </div>}

                </section>
            </div>
        </>);

}

export default AllTrelloTasks;
